import {
  Box,
  Button,
  Collapse,
  HStack,
  Skeleton,
  SkeletonCircle,
  Stack,
  useDisclosure,
  useToast,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { UserAvatar } from 'components/UserAvatar';
import useCallbackOnce from 'hooks/useCallbackOnce';
import useFetchKey from 'hooks/useFetchKey';
import useSubmitMessage from 'hooks/useSubmitMessage';
import React, { Suspense, useCallback, useState } from 'react';
import { ConnectionHandler } from 'utils/graphClient';
import { RemirrorJSON } from 'remirror';
import { isEmptyMessage } from 'utils/editor';
import {
  graphql,
  useFragment,
  useMutation,
  useSafeRelay,
} from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import { useSession } from 'utils/sessionProvider';
import { CommentsThread } from './CommentsThread';
import CommentEditor from './components/Editor';
import {
  AnnouncementComments_chat_createCommentOnAnnouncementMutation,
  MessageType,
} from './__generated__/AnnouncementComments_chat_createCommentOnAnnouncementMutation.graphql';
import { AnnouncementComments_chat_SocialThreadCommentsFragment$key } from './__generated__/AnnouncementComments_chat_SocialThreadCommentsFragment.graphql';
import { AnnouncementComments_member_Query } from './__generated__/AnnouncementComments_member_Query.graphql';

const memberQuery = graphql`
  query AnnouncementComments_member_Query($id: ID!) {
    member: node(id: $id) {
      ... on Member {
        id
        name
        email
        ...UserAvatar_member
        ...UserHandle_member
      }
    }
  }
`;

const socialThreadCommentsFragment = graphql`
  fragment AnnouncementComments_chat_SocialThreadCommentsFragment on SocialThread {
    id
    reference
    ...CommentsThread_chat_socialThread
  }
`;

const createCommentOnAnnouncementMutation = graphql`
  mutation AnnouncementComments_chat_createCommentOnAnnouncementMutation(
    $input: CreateThreadCommentInput!
  ) {
    createThreadComment(input: $input) {
      id
      type
      senderId
      text
      createdAt
      isDelivered
      ...MessageReactions_chat_messageFragment
    }
  }
`;

interface AnnouncementCommentsProps {
  announcementId: string;
  spaceId: string;
  socialThread: AnnouncementComments_chat_SocialThreadCommentsFragment$key;
}

const UnsuspendedAnnouncementComments: React.FC<AnnouncementCommentsProps> = ({
  announcementId,
  spaceId,
  socialThread,
}) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const [newCommentsIds, setNewCommentsIds] = useState<string[]>([]);
  const toast = useToast();
  const { member } = useSession();
  const [editorKey, resetEditorKey] = useFetchKey();
  const [content, setContent] = useState<RemirrorJSON | undefined>(undefined);
  const { onOpen: onShowCommentButton, isOpen: isCommentButtonVisible } =
    useDisclosure();
  const onChange = useCallback((stringifiedMessage?: string) => {
    const message = JSON.parse(stringifiedMessage || '');
    setContent(isEmptyMessage(message) ? undefined : message);
  }, []);
  const { member: currentMember } =
    useLazyLoadQuery<AnnouncementComments_member_Query>(memberQuery, {
      id: member?.id || '',
    });

  const _socialThread = useFragment(socialThreadCommentsFragment, socialThread);

  const [_createThreadComment, createThreadCommentLoading] =
    useMutation<AnnouncementComments_chat_createCommentOnAnnouncementMutation>(
      createCommentOnAnnouncementMutation,
    );

  const createThreadComment = useCallback(
    (message: RemirrorJSON) => {
      const updater = (store: any) => {
        const comment = store.getRootField('createThreadComment');
        const thread = store.get(_socialThread.id);

        if (thread) {
          const comments = ConnectionHandler.getConnection(
            thread,
            'CommentsThread_comments',
          );

          if (comments) {
            const edges = comments.getLinkedRecords('edges');
            const commentEdge = ConnectionHandler.createEdge(
              store,
              comments,
              comment,
              'CommentEdge',
            );

            const newEdges = [...(edges || []), commentEdge].filter(notEmpty);

            comments.setLinkedRecords(newEdges, 'edges');
          }
        }
      };

      _createThreadComment({
        variables: {
          input: {
            referenceId: announcementId,
            text: JSON.stringify(message),
          },
        },
        onCompleted: (_response, errors) => {
          if (errors && errors.length > 0) {
            toast({
              status: 'error',
              title: t`Unable to submit the comment, please try again later.`,
            });
          } else {
            setNewCommentsIds((ids) => [
              ...ids,
              _response.createThreadComment.id,
            ]);
          }
        },
        optimisticResponse: {
          createThreadComment: {
            id: `optimistic-${Date.now()}`,
            type: 'USER' as MessageType,
            senderId: currentMember?.id,
            text: JSON.stringify(message),
            createdAt: new Date().toISOString(),
            isDelivered: false,
            reactions: {
              edges: [],
            },
          },
        },
        updater,
        optimisticUpdater: updater,
      });
    },
    [announcementId, spaceId],
  );

  const handleSubmit = useCallbackOnce(() => {
    if (content) {
      createThreadComment(content);
      resetEditorKey();
      setContent(undefined);
    }
  }, [content]);

  const { triggerSubmit } = useSubmitMessage(handleSubmit);

  return (
    <Stack spacing={4}>
      <CommentsThread
        socialThread={_socialThread}
        newCommentsIds={newCommentsIds}
      />

      <HStack alignItems="flex-start" w="100%">
        {currentMember && (
          <UserAvatar member={currentMember} size="xs" opacity={1} />
        )}

        <Stack w="100%">
          <CommentEditor
            chatKey={editorKey}
            onChange={onChange}
            sendMessage={triggerSubmit}
            spaceId={spaceId}
            onFocus={onShowCommentButton}
          />

          <Box alignSelf="flex-end" flexShrink={1}>
            <Collapse in={isCommentButtonVisible}>
              <Box mb="2">
                <Button
                  size="sm"
                  onClick={handleSubmit}
                  data-qa="announcement-card-comment-button"
                >
                  {t`Comment`}
                </Button>
              </Box>
            </Collapse>
          </Box>
        </Stack>
      </HStack>
    </Stack>
  );
};

export const AnnouncementComments: React.FC<AnnouncementCommentsProps> = (
  props,
) => {
  return (
    <Suspense fallback={<AnnouncementCommentsSkeleton />}>
      <UnsuspendedAnnouncementComments {...props} />
    </Suspense>
  );
};

export const AnnouncementCommentsSkeleton = () => (
  <Box>
    <HStack alignItems="flex-start" w="100%">
      <SkeletonCircle width={6} height={6} />

      <Stack w="100%">
        <Skeleton width="100%" h="60px" borderRadius="md" />
      </Stack>
    </HStack>
  </Box>
);
